/**
 * Physical constraint checks. Violations are FLAGGED, never silently fixed —
 * a planner needs to see what the model had to bend.
 *
 *   1. Single-line crossing. Trains cross only where there are two roads, and
 *      never mid-section. A short hold in a loop is a NORMAL crossing meet;
 *      only a hold longer than a loop can absorb is fatal. (V2 initially
 *      treated every meet as fatal and threw away perfectly good timetables.)
 *   2. Stock balance. Stations without stabling cannot hold a rake overnight
 *      and have none pre-positioned: a departure can only leave if a rake has
 *      already arrived and turned, and nothing may be left there at close.
 *   3. Network Rail paths. A hard external cap. New in V3 and the binding
 *      constraint on Whitby traffic.
 */
import type {
  CrossingConflict, Departure, Network, PathUsage, StockImbalance,
} from './types'
import { fmtMin } from './demand'
import { locoTurnAt, nearestStable, nonStableStations, segmentLabel, journeyMin } from './network'

/** The longest hold a loop (or a terminus platform) can sensibly absorb. */
export const MAX_CROSS_WAIT = 20

/** [enter, exit] occupancy of each line section for one leg. */
function sectionOccupancy(net: Network, leg: Departure): { sec: number; enter: number; exit: number }[] {
  const out: { sec: number; enter: number; exit: number }[] = []
  const lo = Math.min(leg.o, leg.d)
  const hi = Math.max(leg.o, leg.d)
  for (let sec = lo; sec < hi; sec++) {
    const northbound = leg.d > leg.o
    const enterSt = northbound ? sec : sec + 1
    const exitSt = northbound ? sec + 1 : sec
    out.push({
      sec,
      enter: leg.minutes + Math.abs(net.cumMin[enterSt] - net.cumMin[leg.o]),
      exit: leg.minutes + Math.abs(net.cumMin[exitSt] - net.cumMin[leg.o]),
    })
  }
  return out
}

export function crossingConflicts(net: Network, deps: Departure[]): CrossingConflict[] {
  const legs = deps.filter((d) => d.enabled && Number.isFinite(d.minutes))
  const conflicts: CrossingConflict[] = []
  const label = (l: Departure) => `${net.stations[l.o].name} → ${net.stations[l.d].name}`

  for (let i = 0; i < legs.length; i++) {
    const occA = sectionOccupancy(net, legs[i])
    for (let j = i + 1; j < legs.length; j++) {
      const occB = sectionOccupancy(net, legs[j])
      for (const a of occA) {
        const b = occB.find((x) => x.sec === a.sec)
        if (!b) continue
        const overlap = Math.min(a.exit, b.exit) - Math.max(a.enter, b.enter)
        if (overlap <= 0.01) continue
        // The later-entering train holds at its entry to the section until the
        // other clears — a normal crossing meet if the wait is short.
        const later = a.enter >= b.enter ? { leg: legs[i], occ: a, other: b } : { leg: legs[j], occ: b, other: a }
        const waitMin = later.other.exit - later.occ.enter
        const northbound = later.leg.d > later.leg.o
        const holdSt = northbound ? a.sec : a.sec + 1
        // A hold is only possible where there are two roads.
        const canHold = net.crossingPoints.includes(holdSt)
        conflicts.push({
          aTime: legs[i].time,
          aRoute: label(legs[i]),
          bTime: legs[j].time,
          bRoute: label(legs[j]),
          section: segmentLabel(net, a.sec),
          atMin: Math.max(a.enter, b.enter),
          headOn: Math.sign(legs[i].d - legs[i].o) !== Math.sign(legs[j].d - legs[j].o),
          waitMin: Math.max(0, waitMin),
          holdAt: net.stations[holdSt].name,
          resolvable: canHold && waitMin <= MAX_CROSS_WAIT,
        })
        break // one conflict per pair is enough to report
      }
    }
  }
  return conflicts.sort((a, b) => a.atMin - b.atMin)
}

export const fatalConflicts = (net: Network, deps: Departure[]): number =>
  crossingConflicts(net, deps).filter((c) => !c.resolvable).length

// ---------------------------------------------------------------------------
//  Stock balance
// ---------------------------------------------------------------------------

export function stockImbalances(net: Network, deps: Departure[]): StockImbalance[] {
  const out: StockImbalance[] = []
  const legs = deps.filter((d) => d.enabled && Number.isFinite(d.minutes))
  for (const st of nonStableStations(net)) {
    const events: { t: number; delta: 1 | -1; label: string }[] = []
    for (const l of legs) {
      if (l.o === st) events.push({ t: l.minutes, delta: -1, label: l.time })
      if (l.d === st) {
        // Measured arrival + the measured loco turn (LOCO_TURN_MIN via
        // locoTurnAt) — the 30-minute design turnaround falsely flagged the
        // real WTT: 1T10 arrives Whitby 09:45 and its rake works the 10:00.
        events.push({
          t: (l.arrMin ?? l.minutes + journeyMin(net, l.o, l.d)) + locoTurnAt(net, st),
          delta: 1,
          label: l.time,
        })
      }
    }
    if (events.length === 0) continue
    // An arrival ready exactly at a departure time can work it.
    events.sort((a, b) => a.t - b.t || b.delta - a.delta)
    let bal = 0
    let lastReady = 0
    for (const e of events) {
      bal += e.delta
      if (e.delta === 1) lastReady = e.t
      if (bal < 0) {
        out.push({
          station: st,
          kind: 'need',
          atMin: e.t,
          label: e.label,
          count: 1,
          msg: `${net.stations[st].name}: the ${e.label} departs before any rake has arrived — it needs a positioning working from ${net.stations[nearestStable(net, st)].name} first`,
        })
        bal = 0
      }
    }
    if (bal > 0) {
      out.push({
        station: st,
        kind: 'overnight',
        atMin: lastReady,
        label: fmtMin(lastReady),
        // How many rakes are actually stranded — the repair loop scores a
        // candidate by RAKES rescued, so a move that saves one of two is
        // progress rather than "fixed nothing".
        count: bal,
        msg: `${net.stations[st].name}: ${bal} rake${bal === 1 ? '' : 's'} left overnight — no stable there; a return/positioning working out is needed`,
      })
    }
  }
  return out
}

// ---------------------------------------------------------------------------
//  Network Rail paths
//
//  The hard external cap on Whitby traffic, and the reason Mode B is worth
//  modelling at all: if the paths are the binding constraint, then what matters
//  is how many PEOPLE each path carries, not how many trains we own.
// ---------------------------------------------------------------------------

export function pathUsage(net: Network, deps: Departure[]): PathUsage[] {
  const legs = deps.filter((d) => d.enabled && Number.isFinite(d.minutes))
  const out: PathUsage[] = []
  net.segments.forEach((seg, i) => {
    if (seg.pathsPerDay == null) return
    let traversals = 0
    for (const l of legs) {
      const lo = Math.min(l.o, l.d)
      const hi = Math.max(l.o, l.d)
      if (seg.from >= lo && seg.to <= hi) traversals += 1
    }
    const used = traversals * (seg.pathsPerTraversal ?? 1)
    out.push({
      segment: segmentLabel(net, i),
      owner: seg.owner,
      used,
      available: seg.pathsPerDay,
      over: used > seg.pathsPerDay,
      traversals,
    })
  })
  return out
}

export const pathsExceeded = (net: Network, deps: Departure[]): boolean =>
  pathUsage(net, deps).some((p) => p.over)

/**
 * How many paths over budget a timetable is, in total.
 *
 * Best fit must test this MARGINALLY, not absolutely. `pathsExceeded` answers
 * "is this timetable over?", so once a timetable is over budget every further
 * candidate looks illegal — including ones that never touch Network Rail. That
 * silently kills the rescue loop. Comparing overage before and after an add asks
 * the right question: does THIS working make the path problem worse?
 */
export const pathOverage = (net: Network, deps: Departure[]): number =>
  pathUsage(net, deps).reduce((t, p) => t + Math.max(0, p.used - p.available), 0)

/**
 * Would adding this departure break the path budget? Used by Best fit so the
 * constructor never proposes a timetable Network Rail would not accept.
 */
export function pathBudgetOk(net: Network, deps: Departure[], candidate: Departure): boolean {
  return !pathsExceeded(net, [...deps, candidate])
}

// ---------------------------------------------------------------------------
//  Transfer capacity
// ---------------------------------------------------------------------------

export interface TransferWarning { station: number; msg: string }

/**
 * Grosmont holding more trainloads at once than it has platforms is a real
 * operational failure — people end up on a platform with nowhere to stand while
 * a third train wants the road.
 */
export function transferWarnings(
  net: Network,
  peakTrainloads: Record<number, number>
): TransferWarning[] {
  const out: TransferWarning[] = []
  for (const t of net.transfers) {
    const peak = peakTrainloads[t.station] ?? 0
    if (peak > t.platforms) {
      out.push({
        station: t.station,
        msg: `${net.stations[t.station].name}: ${peak} trainloads transferring at once but only ${t.platforms} platform${t.platforms === 1 ? '' : 's'} — spread the connections or the interchange backs up`,
      })
    }
  }
  return out
}
